import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { platformApi } from "@/lib/http";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, LogOut, Building2, Search, Ban, RotateCcw, MessageSquare, UserCog, KeyRound, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const StatusBadge = ({ b }) => {
  if (b.suspended) return <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-100 text-red-700">Suspended</span>;
  const s = b.subscription_status;
  const map = {
    active: "bg-emerald-100 text-emerald-700", trialing: "bg-amber-100 text-amber-700",
    past_due: "bg-orange-100 text-orange-700", canceled: "bg-neutral-200 text-neutral-600",
  };
  return <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${map[s] || "bg-neutral-200 text-neutral-600"}`}>{s || "none"}</span>;
};

const trialDays = (t) => {
  if (!t) return null;
  const d = Math.ceil((new Date(t) - new Date()) / 86400000);
  return d >= 0 ? d : null;
};

const SmsSettingsCard = () => {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { platformApi.get("/platform/sms-settings").then(({ data }) => setF(data)).catch(() => {}); }, []);
  if (!f) return null;
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await platformApi.put("/platform/sms-settings", {
        send_mode: f.send_mode, messaging_service_sid: f.messaging_service_sid,
        account_sid_ref: f.account_sid_ref, bundle_sid_ref: f.bundle_sid_ref,
        default_sender_id: f.default_sender_id,
      });
      setF({ ...data, twilio_account_connected: f.twilio_account_connected });
      toast.success("SMS settings saved");
    } catch { toast.error("Couldn't save"); } finally { setSaving(false); }
  };
  const Field = ({ label, value, onChange, placeholder, hint }) => (
    <div>
      <label className="text-xs uppercase tracking-wider text-neutral-500">{label}</label>
      <Input value={value || ""} onChange={onChange} placeholder={placeholder} className="mt-1 h-10 rounded-lg bg-neutral-950 border-neutral-800 text-white" />
      {hint && <div className="text-[11px] text-neutral-500 mt-1">{hint}</div>}
    </div>
  );
  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-5 mb-6" data-testid="platform-sms-settings">
      <div className="flex items-center gap-2 mb-1"><MessageSquare className="h-5 w-5 text-emerald-400" /><span className="font-heading font-bold">Platform SMS</span>
        <span className={`ml-auto text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${f.twilio_account_connected ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{f.twilio_account_connected ? "Twilio connected" : "Not connected"}</span>
      </div>
      <p className="text-neutral-400 text-sm mb-4">Set once. Enter one <span className="text-white font-medium">shared sender name</span> (e.g. your registered <span className="text-white font-medium">azordi</span>) and every clinic's texts send through it — the clinic's own name is written inside the message. No per-clinic registration needed.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Shared sender name (all clinics)" value={f.default_sender_id} onChange={(e) => setF({ ...f, default_sender_id: e.target.value.replace(/[^A-Za-z0-9]/g, "").slice(0, 11) })} placeholder="azordi" hint="Your ACMA/Twilio-registered alphanumeric ID. Used for EVERY clinic. Leave blank to keep texts simulated." />
        <div>
          <label className="text-xs uppercase tracking-wider text-neutral-500">Sending mode</label>
          <select value={f.send_mode} onChange={(e) => setF({ ...f, send_mode: e.target.value })} className="mt-1 w-full h-10 rounded-lg bg-neutral-950 border border-neutral-800 text-white px-3 text-sm" data-testid="sms-send-mode">
            <option value="alphanumeric">Alphanumeric sender ID (the shared name above)</option>
            <option value="messaging_service">Messaging Service — route all via one MG service</option>
          </select>
          <div className="text-[11px] text-neutral-500 mt-1">Alphanumeric = the Timely model: one shared sender, clinic name in the message body. Clients can't reply.</div>
        </div>
        <Field label="Messaging Service SID (optional)" value={f.messaging_service_sid} onChange={(e) => setF({ ...f, messaging_service_sid: e.target.value })} placeholder="MG…" hint="Only used when mode is Messaging Service." />
        <Field label="Account SID (for your records)" value={f.account_sid_ref} onChange={(e) => setF({ ...f, account_sid_ref: e.target.value })} placeholder="AC…" />
        <Field label="Bundle SID (for your records)" value={f.bundle_sid_ref} onChange={(e) => setF({ ...f, bundle_sid_ref: e.target.value })} placeholder="BU…" />
      </div>
      <Button onClick={save} disabled={saving} className="mt-4 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="sms-settings-save">{saving ? "Saving…" : "Save SMS settings"}</Button>
    </div>
  );
};

export default function PlatformDashboard() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [om, setOm] = useState(null); // owner-manager modal: {biz, users, oldEmail, newEmail, newPassword, newName, busy}

  const openOwner = async (b) => {
    setOm({ biz: b, users: [], oldEmail: "", newEmail: "", newPassword: "", newName: "Owner", busy: true });
    try {
      const { data } = await platformApi.get(`/platform/businesses/${b.id}/users`);
      setOm((s) => s && { ...s, users: data.users || [], busy: false });
    } catch { setOm((s) => s && { ...s, busy: false }); toast.error("Couldn't load clinic users"); }
  };
  const setOmField = (k, v) => setOm((s) => s && { ...s, [k]: v });
  const saveOwner = async () => {
    if (!om) return;
    if (!om.newEmail.trim() || om.newPassword.length < 6) { toast.error("Enter a new owner email and a password (6+ chars)"); return; }
    setOm((s) => ({ ...s, busy: true }));
    try {
      const { data } = await platformApi.post(`/platform/businesses/${om.biz.id}/reset-owner`, {
        new_email: om.newEmail.trim(), new_password: om.newPassword,
        new_name: om.newName || "Owner", old_email: om.oldEmail || undefined,
      });
      toast.success(`Owner ${data.action}: ${data.email}`);
      setOm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't set owner"); setOm((s) => s && { ...s, busy: false }); }
  };
  const removeUser = async (u) => {
    if (!om) return;
    if (!window.confirm(`Remove ${u.email} from ${om.biz.name}? This deletes that login.`)) return;
    try {
      await platformApi.delete(`/platform/businesses/${om.biz.id}/users/${u.id}`);
      toast.success(`Removed ${u.email}`);
      const { data } = await platformApi.get(`/platform/businesses/${om.biz.id}/users`);
      setOm((s) => s && { ...s, users: data.users || [] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't remove user"); }
  };

  const load = () => {
    platformApi.get("/platform/businesses").then(({ data }) => setRows(data)).catch((e) => {
      if (e.response?.status === 401 || e.response?.status === 403) navigate("/platform/login");
    });
    platformApi.get("/platform/stats").then(({ data }) => setStats(data)).catch(() => {});
  };
  useEffect(() => {
    if (!localStorage.getItem("azordi_platform_token")) { navigate("/platform/login"); return; }
    load();
    // eslint-disable-next-line
  }, []);

  const logout = () => { localStorage.removeItem("azordi_platform_token"); navigate("/platform/login"); };

  const toggle = async (b) => {
    const action = b.suspended ? "reactivate" : "suspend";
    if (!b.suspended && !window.confirm(`Suspend ${b.name}? Their team won't be able to log in until reactivated.`)) return;
    try {
      await platformApi.post(`/platform/businesses/${b.id}/${action}`);
      toast.success(b.suspended ? "Clinic reactivated" : "Clinic suspended");
      load();
    } catch { toast.error("Action failed"); }
  };

  const filtered = (rows || []).filter((b) =>
    !q || (b.name || "").toLowerCase().includes(q.toLowerCase()) || (b.owner_email || "").toLowerCase().includes(q.toLowerCase()));

  const Stat = ({ label, value, accent }) => (
    <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-4">
      <div className="text-neutral-400 text-xs uppercase tracking-wider">{label}</div>
      <div className={`font-heading font-extrabold text-2xl mt-1 ${accent || "text-white"}`}>{value}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-neutral-950 text-white" data-testid="platform-dashboard">
      <header className="border-b border-neutral-800 px-6 py-4 flex items-center justify-between sticky top-0 bg-neutral-950/90 backdrop-blur z-10">
        <div className="flex items-center gap-2"><ShieldCheck className="h-6 w-6 text-emerald-400" /><span className="font-heading font-extrabold text-lg">Azordi Operations</span></div>
        <Button variant="outline" size="sm" className="rounded-lg bg-transparent border-neutral-700 text-white hover:bg-neutral-800" onClick={logout} data-testid="platform-logout"><LogOut className="h-4 w-4 mr-1.5" />Sign out</Button>
      </header>

      <div className="max-w-6xl mx-auto p-6">
        <SmsSettingsCard />
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          <Stat label="Clinics" value={stats?.total ?? "—"} />
          <Stat label="Subscribed" value={stats?.subscribed ?? "—"} accent="text-emerald-400" />
          <Stat label="On trial" value={stats?.trialing ?? "—"} accent="text-amber-400" />
          <Stat label="Suspended" value={stats?.suspended ?? "—"} accent="text-red-400" />
          <Stat label="MRR (AUD)" value={stats ? `$${stats.mrr_aud.toLocaleString()}` : "—"} accent="text-emerald-400" />
        </div>

        <div className="flex items-center gap-2 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clinic or owner email" className="h-10 rounded-lg pl-9 bg-neutral-900 border-neutral-800 text-white" data-testid="platform-search" />
          </div>
          <span className="text-neutral-400 text-sm">{filtered.length} clinic{filtered.length === 1 ? "" : "s"}</span>
        </div>

        <div className="bg-neutral-900 border border-neutral-800 rounded-xl overflow-hidden" data-testid="platform-clinics">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead><tr className="text-left text-[10px] uppercase tracking-wider text-neutral-500 border-b border-neutral-800">
                <th className="py-3 px-4 font-medium">Clinic</th><th className="py-3 px-2 font-medium">Owner</th>
                <th className="py-3 px-2 font-medium">Status</th><th className="py-3 px-2 font-medium">Joined</th>
                <th className="py-3 px-2 font-medium text-right">Clients</th><th className="py-3 px-2 font-medium text-right">Bookings</th>
                <th className="py-3 px-4 font-medium text-right">Action</th>
              </tr></thead>
              <tbody>
                {rows === null && <tr><td colSpan={7} className="py-10 text-center text-neutral-500">Loading…</td></tr>}
                {rows !== null && filtered.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-neutral-500">No clinics found.</td></tr>}
                {filtered.map((b) => {
                  const days = trialDays(b.trial_end);
                  return (
                    <tr key={b.id} className="border-b border-neutral-800/60 last:border-0 hover:bg-neutral-800/30" data-testid={`platform-clinic-${b.id}`}>
                      <td className="py-3 px-4"><div className="font-medium flex items-center gap-2"><Building2 className="h-4 w-4 text-neutral-500" />{b.name}</div><div className="text-neutral-500 text-xs">/{b.slug}</div></td>
                      <td className="py-3 px-2 text-neutral-300">{b.owner_email || "—"}</td>
                      <td className="py-3 px-2"><StatusBadge b={b} />{b.subscription_status === "trialing" && days !== null && <span className="text-neutral-500 text-xs ml-1">{days}d left</span>}</td>
                      <td className="py-3 px-2 text-neutral-400">{(b.created_at || "").slice(0, 10)}</td>
                      <td className="py-3 px-2 text-right tabular-nums">{b.clients}</td>
                      <td className="py-3 px-2 text-right tabular-nums">{b.bookings}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="inline-flex gap-2">
                          <Button size="sm" variant="outline" className="rounded-lg bg-transparent border-neutral-700 text-neutral-200 hover:bg-neutral-800" onClick={() => openOwner(b)} data-testid={`platform-owner-${b.id}`}>
                            <UserCog className="h-3.5 w-3.5 mr-1" />Owner
                          </Button>
                          <Button size="sm" variant="outline" className={`rounded-lg bg-transparent ${b.suspended ? "border-emerald-700 text-emerald-400 hover:bg-emerald-950" : "border-red-800 text-red-400 hover:bg-red-950"}`} onClick={() => toggle(b)} data-testid={`platform-toggle-${b.id}`}>
                            {b.suspended ? <><RotateCcw className="h-3.5 w-3.5 mr-1" />Reactivate</> : <><Ban className="h-3.5 w-3.5 mr-1" />Suspend</>}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {om && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => !om.busy && setOm(null)} data-testid="owner-modal">
          <div className="w-full max-w-md bg-neutral-900 border border-neutral-800 rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <UserCog className="h-5 w-5 text-emerald-400" />
              <span className="font-heading font-bold">Manage owner — {om.biz.name}</span>
              <button className="ml-auto text-neutral-500 hover:text-white" onClick={() => setOm(null)} data-testid="owner-modal-close"><X className="h-5 w-5" /></button>
            </div>
            <p className="text-neutral-400 text-sm mb-4">Set the real owner login for this clinic. Optionally rename a leftover/demo login into the real owner so nothing stale is left behind.</p>

            <div className="mb-4">
              <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">Current logins</div>
              <div className="space-y-1.5 max-h-40 overflow-auto">
                {om.busy && om.users.length === 0 && <div className="text-neutral-500 text-sm">Loading…</div>}
                {om.users.map((u) => (
                  <div key={u.id} className="flex items-center gap-2 text-sm bg-neutral-950 border border-neutral-800 rounded-lg px-3 py-2" data-testid={`owner-user-${u.id}`}>
                    <span className="truncate">{u.email}</span>
                    <span className="text-[10px] uppercase tracking-wide text-neutral-500">{u.role}</span>
                    <button className="ml-auto text-neutral-500 hover:text-emerald-400 flex items-center gap-1 text-xs" onClick={() => setOmField("oldEmail", u.email)} data-testid={`owner-pick-${u.id}`} title="Rename this login into the new owner"><KeyRound className="h-3.5 w-3.5" />Rename</button>
                    <button className="text-neutral-500 hover:text-red-400" onClick={() => removeUser(u)} data-testid={`owner-remove-${u.id}`} title="Delete this login"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                {!om.busy && om.users.length === 0 && <div className="text-neutral-500 text-sm">No users found.</div>}
              </div>
            </div>

            <div className="space-y-3">
              {om.oldEmail && (
                <div className="flex items-center gap-2 text-xs bg-emerald-950/40 border border-emerald-900 rounded-lg px-3 py-2 text-emerald-300" data-testid="owner-rename-note">
                  Renaming <span className="font-mono">{om.oldEmail}</span> → new owner below
                  <button className="ml-auto text-emerald-400 hover:text-white" onClick={() => setOmField("oldEmail", "")}>clear</button>
                </div>
              )}
              <div>
                <label className="text-xs uppercase tracking-wider text-neutral-500">New owner name</label>
                <Input value={om.newName} onChange={(e) => setOmField("newName", e.target.value)} placeholder="Owner name" className="mt-1 h-10 rounded-lg bg-neutral-950 border-neutral-800 text-white" data-testid="owner-new-name" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-neutral-500">New owner email</label>
                <Input value={om.newEmail} onChange={(e) => setOmField("newEmail", e.target.value)} placeholder="owner@theclinic.com" className="mt-1 h-10 rounded-lg bg-neutral-950 border-neutral-800 text-white" data-testid="owner-new-email" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-neutral-500">New owner password (6+ chars)</label>
                <Input value={om.newPassword} onChange={(e) => setOmField("newPassword", e.target.value)} placeholder="••••••••" className="mt-1 h-10 rounded-lg bg-neutral-950 border-neutral-800 text-white" data-testid="owner-new-password" />
              </div>
              <Button onClick={saveOwner} disabled={om.busy} className="w-full rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="owner-save">{om.busy ? "Saving…" : (om.oldEmail ? "Rename & set as owner" : "Set owner login")}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
